import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, X, ChevronLeft, ChevronRight, ChevronUp, Package, ChevronDown, Calendar } from 'lucide-react';

// Styled Components
const ProfileTabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0 1rem 0;
  padding: 0.5rem;
  position: relative;
  z-index: 2;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const ProfileTab = styled.button`
  background: ${props => props.active ? 
    props.theme?.colors?.accent || '#800000' : 'transparent'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  color: ${props => props.active ? 
    'white' : props.theme?.colors?.accent || '#800000'};
  padding: 0.75rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 768px) {
    padding: 0.875rem 2.5rem;
    font-size: 1rem;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (hover: hover) {
    &:hover {
      background: ${props => props.active ? 
        props.theme?.colors?.primary || '#4A0404' : 
        `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
  align-items: start;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 3rem;
  }

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin: 2rem 0;
    padding: 0 0.5rem;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0;
  margin: 0;
  transition: background 0.2s ease;
  min-height: auto;
  
  &:hover {
    background: ${props => `${props.theme?.colors?.accent}10` || 'rgba(128, 0, 0, 0.1)'};
  }
  
  h3 {
    margin: 0;
    padding: 0;
    font-size: 0.9rem;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    line-height: 1.2;
    flex: 1;
    
    @media (min-width: 768px) {
      font-size: 0.95rem;
    }
  }
`;

const ExpandButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:active {
    transform: scale(0.9);
  }
`;

const ItemDetails = styled.div`
  max-height: ${props => props.expanded ? '2000px' : '0'};
  opacity: ${props => props.expanded ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  
  .details-content {
    padding-top: ${props => props.expanded ? '0.75rem' : '0'};
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    transition: all 0.3s ease;
  }
`;

const ProfileContent = ({ 
  theme, 
  posts = [], 
  events = [], 
  onAddPost, 
  onAddEvent,
  onEditPost,
  onEditEvent,
  onDeletePost,
  onDeleteEvent,
  onPostUpdate,
  onEventUpdate,
  expandedItems,
  toggleItemExpansion,
  handleMediaUpload,
  MobileItemCard,
  MobileItemImageContainer,
  MobileItemContent,
  MobileDeleteButton,
  DesktopItemCard,
  ItemImageContainer,
  ItemContent,
  DeleteButton,
  ValidatedEditableText,
  AddressInput,
  CategorySelect,
  handleOpenMapForSelection
}) => {
  const [activeSubTab, setActiveSubTab] = useState('posts');

  const isVideoUrl = (url) => {
    if (!url) return false;
    if (typeof url === 'object' && url.isVideo) return true;
    if (typeof url === 'string') {
      if (url.includes('.mp4') || url.includes('.webm') || url.includes('.mov')) return true;
      if (url.includes('video-') || url.includes('%2Fvideo-')) return true;
    }
    return false;
  };

  const renderPlaceholderCard = (type) => (
    <React.Fragment key={`add-${type}-placeholder`}>
      {/* Mobile Placeholder */}
      {window.innerWidth < 767 && (
        <MobileItemCard
          theme={theme}
          onClick={type === 'post' ? onAddPost : onAddEvent}
          style={{
            cursor: 'pointer',
            border: `2px dashed ${theme?.colors?.accent}40`,
            background: `${theme?.colors?.surface}20`
          }}
        >
          <MobileItemImageContainer theme={theme}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              color: theme?.colors?.accent,
              opacity: 0.6,
              height: '100%',
              width: '100%'
            }}>
              {type === 'post' ? <Plus size={32} /> : <Calendar size={32} />}
              <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                Add {type === 'post' ? 'Post' : 'Event'}
              </span>
            </div>
          </MobileItemImageContainer>
          
          <MobileItemContent theme={theme}>
            <div className="item-name" style={{ opacity: 0.5, textAlign: 'center' }}>
              Tap to add
            </div>
          </MobileItemContent>
        </MobileItemCard>
      )}

      {/* Desktop Placeholder */}
      {window.innerWidth >= 767 && (
        <DesktopItemCard 
          theme={theme}
          onClick={type === 'post' ? onAddPost : onAddEvent}
          style={{
            cursor: 'pointer',
            border: `2px dashed ${theme?.colors?.accent}40`,
            background: `${theme?.colors?.surface}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px'
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            color: theme?.colors?.accent,
            opacity: 0.7,
            padding: '2rem'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `${theme?.colors?.accent}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease'
            }}>
              {type === 'post' ? <Plus size={40} /> : <Calendar size={40} />}
            </div>
            <span style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              textAlign: 'center'
            }}>
              Add {type === 'post' ? 'Post' : 'Event'}
            </span>
          </div>
        </DesktopItemCard>
      )}
    </React.Fragment>
  );

  const renderPostCard = (post) => {
    const isExpanded = expandedItems.has(post.id);
    const validImages = post.images?.filter(Boolean) || [];
    const currentImageIndex = post.currentImageIndex || 0;
    const currentMedia = validImages[currentImageIndex] || null;
    const isVideo = isVideoUrl(currentMedia);

    return (
      <React.Fragment key={post.id}>
        {/* Mobile Post Card */}
        <MobileItemCard
          theme={theme}
          onClick={() => {
            console.log('📱 Mobile card clicked - opening edit modal');
            onEditPost(post); // This should trigger the modal
          }}
        >
          <MobileItemImageContainer
            theme={theme}
            $bgImage={!isVideo && currentMedia ? (typeof currentMedia === 'string' ? currentMedia : currentMedia.preview) : null}
          >
            {currentMedia ? (
              isVideo ? (
                <video
                  src={typeof currentMedia === 'string' ? currentMedia : currentMedia.preview}
                  loop
                  muted
                  playsInline
                  autoPlay
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    position: 'relative',
                    zIndex: 1
                  }}
                />
              ) : (
                <img
                  src={typeof currentMedia === 'string' ? currentMedia : currentMedia.preview}
                  alt={post.caption || 'Post'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    position: 'relative',
                    zIndex: 1
                  }}
                />
              )
            ) : (
              <div className="placeholder">
                <Package size={24} />
                <span>Add Media</span>
              </div>
            )}

            {validImages.length > 1 && (
              <>
                <button 
                  className="mobile-carousel-arrow left"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = ((currentImageIndex - 1) + validImages.length) % validImages.length;
                    onPostUpdate(post.id, { currentImageIndex: newIndex });
                  }}
                >
                  <ChevronLeft size={12} />
                </button>
                <button 
                  className="mobile-carousel-arrow right"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = (currentImageIndex + 1) % validImages.length;
                    onPostUpdate(post.id, { currentImageIndex: newIndex });
                  }}
                >
                  <ChevronRight size={12} />
                </button>
              </>
            )}

            <MobileDeleteButton
              onClick={(e) => {
                e.stopPropagation();
                onDeletePost(post.id);
              }}
            >
              <X size={12} />
            </MobileDeleteButton>
          </MobileItemImageContainer>
          
          <MobileItemContent theme={theme}>
            <div className="item-name">
              {post.caption || <span style={{ opacity: 0.5 }}>Add caption</span>}
            </div>
          </MobileItemContent>
        </MobileItemCard>

        {/* Desktop Post Card */}
        <DesktopItemCard theme={theme}>
          <DeleteButton 
            onClick={() => onDeletePost(post.id)}
            theme={theme}
          >
            <X size={16} />
          </DeleteButton>
          
          <ItemImageContainer
            theme={theme}
            $bgImage={!isVideo && currentMedia ? (typeof currentMedia === 'string' ? currentMedia : currentMedia.preview) : null}
            style={{ cursor: !currentMedia ? 'pointer' : 'default' }}
            onClick={() => {
              if (!currentMedia) {
                document.getElementById(`post-upload-${post.id}-0`).click();
              }
            }}
          >
            <div className="image-container">
              {currentMedia ? (
                isVideo ? (
                  <video
                    src={typeof currentMedia === 'string' ? currentMedia : currentMedia.preview}
                    loop
                    muted
                    playsInline
                    autoPlay
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      position: 'relative',
                      zIndex: 1
                    }}
                  />
                ) : (
                  <img
                    src={typeof currentMedia === 'string' ? currentMedia : currentMedia.preview}
                    alt={post.caption || 'Post'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      position: 'relative',
                      zIndex: 1
                    }}
                  />
                )
              ) : (
                <div className="placeholder">
                  <Plus size={24} />
                  <span>Upload Image or Video</span>
                </div>
              )}
            </div>

            {validImages.length > 0 && (
              <>
                <button 
                  className="carousel-arrow left"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = ((currentImageIndex - 1) + 3) % 3;
                    onPostUpdate(post.id, { currentImageIndex: newIndex });
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <button 
                  className="carousel-arrow right"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = (currentImageIndex + 1) % 3;
                    onPostUpdate(post.id, { currentImageIndex: newIndex });
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}

            {currentMedia && (
              <button 
                className="add-image"
                onClick={(e) => {
                  e.stopPropagation();
                  const newImages = [...post.images];
                  newImages[currentImageIndex] = null;
                  onPostUpdate(post.id, { images: newImages });
                }}
              >
                <X size={16} />
              </button>
            )}

            <input
              type="file"
              id={`post-upload-${post.id}-0`}
              accept="image/*,video/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset immediately so re-selecting the same file reliably
                // fires onChange again on mobile instead of silently no-op'ing.
                e.target.value = '';
                if (file) {
                  handleMediaUpload(post.id, 0, file, true);
                }
              }}
            />
          </ItemImageContainer>
          
          <ItemContent>
            <div
              style={{ padding: '1rem', cursor: 'pointer' }}
              onClick={() => onEditPost(post)}
              title="Edit post"
            >
              {post.caption || <span style={{ opacity: 0.5 }}>Add caption</span>}
            </div>
          </ItemContent>
        </DesktopItemCard>
      </React.Fragment>
    );
  };

  const renderEventCard = (event) => {
    const isExpanded = expandedItems.has(event.id);
    const validImages = event.images?.filter(Boolean) || [];
    const currentImageIndex = event.currentImageIndex || 0;
    const currentMedia = validImages[currentImageIndex] || null;

    return (
      <React.Fragment key={event.id}>
        {/* Mobile Event Card */}
        <MobileItemCard
          theme={theme}
          onClick={() => onEditEvent(event)}
        >
          <MobileItemImageContainer 
            theme={theme}
            $bgImage={currentMedia ? (typeof currentMedia === 'string' ? currentMedia : currentMedia.preview) : null}
          >
            {currentMedia ? (
              <img 
                src={typeof currentMedia === 'string' ? currentMedia : currentMedia.preview}
                alt={event.name || 'Event'}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1
                }}
              />
            ) : (
              <div className="placeholder">
                <Calendar size={24} />
                <span>Add Image</span>
              </div>
            )}

            {validImages.length > 1 && (
              <>
                <button 
                  className="mobile-carousel-arrow left"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = ((currentImageIndex - 1) + validImages.length) % validImages.length;
                    onEventUpdate(event.id, { currentImageIndex: newIndex });
                  }}
                >
                  <ChevronLeft size={12} />
                </button>
                <button 
                  className="mobile-carousel-arrow right"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = (currentImageIndex + 1) % validImages.length;
                    onEventUpdate(event.id, { currentImageIndex: newIndex });
                  }}
                >
                  <ChevronRight size={12} />
                </button>
              </>
            )}

            <MobileDeleteButton
              onClick={(e) => {
                e.stopPropagation();
                onDeleteEvent(event.id);
              }}
            >
              <X size={12} />
            </MobileDeleteButton>
          </MobileItemImageContainer>
          
          <MobileItemContent theme={theme}>
            <div className="item-name">
              {event.name || <span style={{ opacity: 0.5 }}>Event Name</span>}
            </div>
            {event.eventDate && (
              <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                📅 {new Date(event.eventDate).toLocaleDateString()}
              </div>
            )}
          </MobileItemContent>
        </MobileItemCard>

        {/* Desktop Event Card */}
        <DesktopItemCard theme={theme}>
          <DeleteButton 
            onClick={() => onDeleteEvent(event.id)}
            theme={theme}
          >
            <X size={16} />
          </DeleteButton>
          
          <ItemImageContainer 
            theme={theme}
            $bgImage={currentMedia ? (typeof currentMedia === 'string' ? currentMedia : currentMedia.preview) : null}
          >
            <div 
              className="image-container"
              onClick={() => {
                if (!currentMedia) {
                  document.getElementById(`event-upload-${event.id}-0`).click();
                }
              }}
              style={{ cursor: !currentMedia ? 'pointer' : 'default' }}
            >
              {currentMedia ? (
                <img 
                  src={typeof currentMedia === 'string' ? currentMedia : currentMedia.preview}
                  alt={event.name || 'Event'}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    position: 'relative',
                    zIndex: 1
                  }}
                />
              ) : (
                <div className="placeholder">
                  <Calendar size={24} />
                  <span>Upload Image</span>
                </div>
              )}
            </div>

            {validImages.length > 0 && (
              <>
                <button 
                  className="carousel-arrow left"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = ((currentImageIndex - 1) + 3) % 3;
                    onEventUpdate(event.id, { currentImageIndex: newIndex });
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <button 
                  className="carousel-arrow right"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = (currentImageIndex + 1) % 3;
                    onEventUpdate(event.id, { currentImageIndex: newIndex });
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}

            {currentMedia && (
              <button 
                className="add-image"
                onClick={(e) => {
                  e.stopPropagation();
                  const newImages = [...event.images];
                  newImages[currentImageIndex] = null;
                  onEventUpdate(event.id, { images: newImages });
                }}
              >
                <X size={16} />
              </button>
            )}

            <input
              type="file"
              id={`event-upload-${event.id}-0`}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset immediately so re-selecting the same file reliably
                // fires onChange again on mobile instead of silently no-op'ing.
                e.target.value = '';
                if (file) {
                  handleMediaUpload(event.id, 0, file, false, true);
                }
              }}
            />
          </ItemImageContainer>
          
          <ItemContent>
            <ItemHeader onClick={() => toggleItemExpansion(event.id)}>
              <h3>
                {event.name || <span style={{ opacity: 0.5 }}>Event Name</span>}
              </h3>
              <ExpandButton>
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </ExpandButton>
            </ItemHeader>

            <ItemDetails expanded={isExpanded}>
              <div className="details-content">
                <ValidatedEditableText
                  value={event.name}
                  onChange={(value) => onEventUpdate(event.id, { name: value })}
                  placeholder="Event Name"
                  theme={theme}
                />

                <ValidatedEditableText
                  value={event.price}
                  onChange={(value) => onEventUpdate(event.id, { price: value })}
                  placeholder="Price (optional)"
                  theme={theme}
                />

                <ValidatedEditableText
                  value={event.description}
                  onChange={(value) => onEventUpdate(event.id, { description: value })}
                  placeholder="Event Description"
                  multiline
                  theme={theme}
                />

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: theme?.colors?.text,
                    fontSize: '0.85rem',
                    opacity: 0.8
                  }}>
                    Event Date
                  </label>
                  <input
                    type="date"
                    value={event.eventDate || ''}
                    onChange={(e) => onEventUpdate(event.id, { eventDate: e.target.value })}
                    style={{
                      width: '100%',
                      background: theme?.colors?.background || '#1a1a1a',
                      border: `1px solid color-mix(in srgb, ${theme?.colors?.accent || '#ffffff'} 40%, transparent)`,
                      borderRadius: '8px',
                      padding: '0.75rem',
                      color: theme?.colors?.text || '#ffffff',
                      colorScheme: 'dark',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: theme?.colors?.text,
                    fontSize: '0.85rem',
                    opacity: 0.8
                  }}>
                    Event Time
                  </label>
                  <input
                    type="time"
                    value={event.eventTime || ''}
                    onChange={(e) => onEventUpdate(event.id, { eventTime: e.target.value })}
                    style={{
                      width: '100%',
                      background: theme?.colors?.background || '#1a1a1a',
                      border: `1px solid color-mix(in srgb, ${theme?.colors?.accent || '#ffffff'} 40%, transparent)`,
                      borderRadius: '8px',
                      padding: '0.75rem',
                      color: theme?.colors?.text || '#ffffff',
                      colorScheme: 'dark',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>

                {CategorySelect && (
                  <CategorySelect
                    value={event.category || 'Other'}
                    onChange={(e) => onEventUpdate(event.id, { category: e.target.value })}
                    theme={theme}
                  >
                    <option value="Concert">Concert</option>
                    <option value="Workshop">Workshop</option>
                    <option value="Meetup">Meetup</option>
                    <option value="Sale">Sale</option>
                    <option value="Launch">Launch</option>
                    <option value="Other">Other</option>
                  </CategorySelect>
                )}

                {AddressInput && (
                  <AddressInput
                    address={event.address || ''}
                    onAddressChange={(value) => onEventUpdate(event.id, { address: value })}
                    onLocationSelect={(location) => {
                      if (location?.coordinates) {
                        onEventUpdate(event.id, {
                          address: location.address,
                          coordinates: location.coordinates,
                          city: location.city
                        });
                      }
                    }}
                    onMapOpen={() => handleOpenMapForSelection(event.id, false, true)}
                  />
                )}
              </div>
            </ItemDetails>

            {!isExpanded && event.eventDate && (
              <div style={{
                padding: '0.75rem',
                fontSize: '0.85rem',
                opacity: 0.7,
                borderTop: `1px solid ${theme?.colors?.accent}20`
              }}>
                📅 {new Date(event.eventDate).toLocaleDateString()}
                {event.eventTime && ` at ${event.eventTime}`}
              </div>
            )}
          </ItemContent>
        </DesktopItemCard>
      </React.Fragment>
    );
  };

  return (
    <>
      {/* Sub-tabs for Posts and Events */}
      <ProfileTabContainer>
        <ProfileTab
          active={activeSubTab === 'posts'}
          onClick={() => setActiveSubTab('posts')}
          theme={theme}
        >
          <Package size={18} />
          Posts
        </ProfileTab>
        <ProfileTab
          active={activeSubTab === 'events'}
          onClick={() => setActiveSubTab('events')}
          theme={theme}
        >
          <Calendar size={18} />
          Events
        </ProfileTab>
      </ProfileTabContainer>

      {/* Content Grid */}
      <ItemGrid>
        {activeSubTab === 'posts' ? (
          <>
            {renderPlaceholderCard('post')}
            {posts.map(post => renderPostCard(post))}
            
            {posts.length === 0 && (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '3rem 1rem',
                color: theme?.colors?.text,
                opacity: 0.6
              }}>
                <p style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                  No posts yet
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            {renderPlaceholderCard('event')}
            {events.map(event => renderEventCard(event))}
            
            {events.length === 0 && (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '3rem 1rem',
                color: theme?.colors?.text,
                opacity: 0.6
              }}>
                <p style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                  No events yet
                </p>
              </div>
            )}
          </>
        )}
      </ItemGrid>
    </>
  );
};

export default ProfileContent;